import { useRef, useState, type FormEvent } from "react";
import { updateProfile } from "firebase/auth";

import {
  Banner,
  Button,
  TextArea,
  TextField,
  focusFirstInvalid,
} from "@ui/react";
import { ServiceAreaPicker } from "../../components/ServiceAreaPicker";
import { SkillsPicker } from "../../components/SkillsPicker";
import { useAuth } from "../../lib/auth";
import { auth, idToken } from "../../lib/firebase";
import { completeRegistration, redeemReferral } from "../../lib/api/contractor";
import type { ServiceAreaGeo } from "../../lib/types/models";
import { OnboardingLayout } from "./OnboardingLayout";
import { errorMessage } from "../../lib/errorMessage";

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  skills?: string;
  areas?: string;
  hourlyRate?: string;
}

/** Splits a Firebase displayName so a returning contractor isn't retyping it. */
function splitName(displayName: string | null | undefined): [string, string] {
  const parts = (displayName ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return ["", ""];
  const [first, ...rest] = parts;
  return [first ?? "", rest.join(" ")];
}

export function ProfileCompletionScreen() {
  const { user, contractor, refresh, signOut } = useAuth();
  const [fallbackFirst, fallbackLast] = splitName(user?.displayName);

  const [firstName, setFirstName] = useState(contractor?.firstName || fallbackFirst);
  const [lastName, setLastName] = useState(contractor?.lastName || fallbackLast);
  const [phoneNumber, setPhoneNumber] = useState(contractor?.phoneNumber ?? "");
  const [businessName, setBusinessName] = useState(contractor?.businessName ?? "");
  const [skills, setSkills] = useState<string[]>(contractor?.skills ?? []);
  const [areas, setAreas] = useState<ServiceAreaGeo[]>(contractor?.serviceAreaGeos ?? []);
  const [hourlyRate, setHourlyRate] = useState(
    contractor?.hourlyRate ? String(Math.round(contractor.hourlyRate)) : "",
  );
  const [bio, setBio] = useState(contractor?.bio ?? "");
  const [referralCode, setReferralCode] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  /* Set when the profile saved but the referral code did not redeem. The screen
   * then stops and hands over a Continue button rather than calling refresh()
   * itself — refresh() moves the gate to the bank step and unmounts this
   * screen, which would take the warning with it before anyone read it. */
  const [referralWarning, setReferralWarning] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const next: FieldErrors = {};
    if (!firstName.trim()) next.firstName = "Enter your first name.";
    if (!lastName.trim()) next.lastName = "Enter your last name.";
    if (!skills.length) next.skills = "Pick at least one trade category.";
    if (!areas.length) next.areas = "Add at least one service area.";
    if (hourlyRate.trim() && !Number.isFinite(Number(hourlyRate))) {
      next.hourlyRate = "Enter a number, or leave this blank.";
    }
    setFieldErrors(next);
    if (Object.keys(next).length) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    setReferralWarning(null);

    const rate = hourlyRate.trim() ? Number(hourlyRate) : undefined;

    try {
      await completeRegistration({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(phoneNumber.trim() ? { phoneNumber: phoneNumber.trim() } : {}),
        ...(businessName.trim() ? { businessName: businessName.trim() } : {}),
        skills,
        serviceAreaGeos: areas.map((area) => ({
          name: area.name,
          ...(area.placeId ? { placeId: area.placeId } : {}),
          latitude: area.latitude,
          longitude: area.longitude,
        })),
        ...(rate !== undefined ? { hourlyRate: rate, currency: "ZAR" } : {}),
        ...(bio.trim() ? { bio: bio.trim() } : {}),
      });

      /* POST /auth/complete is what mints the `userType: "contractor"` claim,
       * and the ID token in hand predates it. Force-refresh BEFORE anything
       * else: the next claim-gated call is GET /banks on the payout screen, and
       * with the cached token it answers 403 for the token's whole hour. This
       * is issue #74 — it cost a day the first time. */
      await idToken(true);

      const current = auth().currentUser;
      if (current) {
        // Cosmetic, and deliberately not awaited into the failure path.
        void updateProfile(current, {
          displayName: `${firstName.trim()} ${lastName.trim()}`.trim(),
        }).catch(() => undefined);
      }

      const code = referralCode.trim();
      if (code) {
        try {
          await redeemReferral(code);
        } catch (thrown) {
          // A bad, expired or already-used code must never cost someone their
          // onboarding — the profile is already saved at this point.
          setReferralWarning(errorMessage(thrown));
          return;
        }
      }

      await refresh();
    } catch (thrown) {
      setFormError(errorMessage(thrown));
    } finally {
      // In a `finally`, not on each path: `refresh()` swallows its own failures
      // and always resolves, so a follow-up GET /profile that fails (cold start,
      // or a 403 while the forced token refresh propagates) leaves the gate
      // unchanged and this screen mounted. Clearing only on the error path left
      // a permanently disabled, spinning button and no way forward.
      setSubmitting(false);
    }
  };

  if (referralWarning) {
    return (
      <OnboardingLayout
        eyebrow="Profile saved"
        title="Your profile is set up."
        lead="One thing didn't go through, but it doesn't hold anything up."
        step={3}
      >
        <div className="stack">
          <Banner tone="warn" title="We couldn't apply that referral code">
            {referralWarning.replace(/[.!?]?$/, ".")} Your profile is saved either way — ask
            whoever referred you to check the code, and you can add it any time from Profile &gt;
            Edit profile.
          </Banner>
          <Button size="lg" onClick={() => void refresh()}>
            Continue to payout details
          </Button>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      eyebrow="Your profile"
      title="Tell us about your work."
      lead="Homeowners see this when you quote. Your trades and service areas decide which jobs reach you."
      step={3}
      footer={
        <Button variant="tertiary" onClick={() => void signOut()} disabled={submitting}>
          Sign out and use a different account
        </Button>
      }
    >
      <form ref={formRef} className="form" onSubmit={onSubmit} noValidate>
        {formError ? <Banner tone="danger" title="Couldn't save your profile">{formError}</Banner> : null}

        <div className="field-grid">
          <TextField
            label="First name"
            autoComplete="given-name"
            autoFocus={!firstName}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            error={fieldErrors.firstName ?? null}
            required
          />
          <TextField
            label="Last name"
            autoComplete="family-name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            error={fieldErrors.lastName ?? null}
            required
          />
        </div>

        <div className="field-grid">
          <TextField
            label="Phone number"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder="082 000 0000"
            autoFocus={Boolean(firstName)}
            value={phoneNumber}
            onChange={(event) => setPhoneNumber(event.target.value)}
          />
          <TextField
            label="Business name"
            autoComplete="organization"
            hint="Optional — leave blank if you trade under your own name."
            value={businessName}
            onChange={(event) => setBusinessName(event.target.value)}
          />
        </div>

        <SkillsPicker value={skills} onChange={setSkills} error={fieldErrors.skills ?? null} />

        <ServiceAreaPicker value={areas} onChange={setAreas} error={fieldErrors.areas ?? null} />

        <TextField
          label="Hourly rate (ZAR)"
          type="number"
          inputMode="numeric"
          min={0}
          step={10}
          placeholder="450"
          hint="Optional. Shown as a guide — every quote you send sets its own price."
          value={hourlyRate}
          onChange={(event) => setHourlyRate(event.target.value)}
          error={fieldErrors.hourlyRate ?? null}
        />

        <TextArea
          label="About you"
          placeholder="Years in the trade, the work you like best, anything a homeowner should know."
          hint="Optional."
          value={bio}
          onChange={(event) => setBio(event.target.value)}
        />

        <TextField
          label="Referral code"
          hint="Optional. If someone referred you to Shoku, enter their code."
          value={referralCode}
          onChange={(event) => setReferralCode(event.target.value.toUpperCase())}
        />

        <Button type="submit" size="lg" loading={submitting}>
          Save and continue
        </Button>
      </form>
    </OnboardingLayout>
  );
}
